import { t } from 'i18next';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Box, Spinner, Text } from 'quotient';
import { SPMModelsService } from 'services';
import { SPMWorksheetService } from 'services/SPM/SPMWorksheetService';
import { PlanningCycle } from 'types/planningCycles';
import {
  NAVIGATION_ANIMATION_DURATION,
  SPMModel,
  SPMModelType,
  SPMScenario,
  SPMWorksheet,
  SPMWorksheetLayerType,
  SPMWorksheetType,
} from 'types/SPM';

export type SPMPlanContextType = {
  planningCycle: PlanningCycle;
  setPlanningCycle: Dispatch<SetStateAction<PlanningCycle>>;
  scenarios: SPMScenario[];
  capacityModel: SPMModel;
  quotaModel: SPMModel;
  capacityWorksheets: SPMWorksheet[];
  quotaWorksheets: SPMWorksheet[];
  baseScenario: SPMScenario;
  planMetricsWorksheet?: SPMWorksheet;
  rosterWorksheet: SPMWorksheet | undefined;
  worksheetsFilter: string | undefined;
  setWorksheetsFilter: Dispatch<SetStateAction<string | undefined>>;
  isNavigationCollapsed: boolean;
  isAnimating: boolean;
  toggleNavigation: () => void;
  focusedWorksheet: string | undefined;
  focusWorksheet: (worksheetId: string) => void;
  unfocusWorksheet: () => void;
};

const SPMPlanContext = createContext<SPMPlanContextType>({} as SPMPlanContextType);
export const useSPMPlanContext = () => useContext(SPMPlanContext);

type Props = {
  children: ReactNode;
  planningCycle: PlanningCycle;
  scenarios: SPMScenario[];
};

export const SPMPlanContextProvider = ({
  children,
  planningCycle: planningCycleProp,
  scenarios: scenariosProp,
}: Props) => {
  const [planningCycle, setPlanningCycle] = useState<PlanningCycle>(planningCycleProp);
  // These models do not contain the columns for the worksheets as that was too much for the planning cycle call. Marking
  // them as internal and refetching with the spmmodelquery below to retrieve the actual columns.
  const _capacityModel = planningCycle.spm_models.find((model) => model.model_type === SPMModelType.CAPACITY);
  const _quotaModel = planningCycle.spm_models.find((model) => model.model_type === SPMModelType.QUOTA);

  // This is used to filter the worksheets either at the plan level or at the model level
  const [worksheetsFilter, setWorksheetsFilter] = useState<string | undefined>(undefined);

  const [isNavigationCollapsed, setIsNavigationCollapsed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  // This is used to track the focused worksheet when the user clicks on the worksheet resizer
  const [focusedWorksheet, setFocusedWorksheet] = useState<string | undefined>(undefined);
  const { query: worksheetsQuery } = SPMWorksheetService.useListSPMWorksheetsQuery({
    planningCycleId: planningCycleProp.id,
  });
  const { query: capacityModelQuery } = SPMModelsService.useGetSPMModelQuery(_capacityModel?.id as string);
  const { query: quotaModelQuery } = SPMModelsService.useGetSPMModelQuery(_quotaModel?.id as string);
  const { query: capacityWorksheetsQuery } = SPMWorksheetService.useListSPMWorksheetsQuery(
    { planningCycleId: planningCycleProp.id, spmModelId: _capacityModel?.id },
    { enabled: !!_capacityModel?.id },
  );
  const { query: quotaWorksheetsQuery } = SPMWorksheetService.useListSPMWorksheetsQuery(
    { planningCycleId: planningCycleProp.id, spmModelId: _quotaModel?.id },
    { enabled: !!_quotaModel?.id },
  );

  const planMetricsWorksheet = useMemo(
    () =>
      worksheetsQuery.data?.data.find(
        (ws) => ws.type === SPMWorksheetType.METRICS && ws.layer === SPMWorksheetLayerType.SCENARIO,
      ),
    [worksheetsQuery.data?.data],
  );

  const rosterWorksheet = useMemo(
    () => capacityWorksheetsQuery.data?.data.find((ws) => ws.type === SPMWorksheetType.ROSTER),
    [capacityWorksheetsQuery.data?.data],
  );

  // Update planningCycle state when the prop changes (e.g., when query is invalidated in parts of the app)
  useEffect(() => {
    setPlanningCycle(planningCycleProp);
  }, [planningCycleProp]);

  const toggleNavigation = useCallback(() => {
    if (isAnimating) {
      // Prevent toggling during animation
      return;
    }

    setIsAnimating(true);
    setIsNavigationCollapsed(!isNavigationCollapsed);

    // Reset animation state after transition completes which lasts 500ms
    setTimeout(() => setIsAnimating(false), NAVIGATION_ANIMATION_DURATION);
  }, [isAnimating, isNavigationCollapsed, setIsAnimating, setIsNavigationCollapsed]);

  const focusWorksheet = useCallback(
    (worksheetId: string) => {
      setFocusedWorksheet(worksheetId);
      if (!isNavigationCollapsed) {
        // Collapse navigation when focusing a worksheet
        toggleNavigation();
      }
    },
    [isNavigationCollapsed, toggleNavigation],
  );

  const unfocusWorksheet = useCallback(() => {
    setFocusedWorksheet(undefined);
    if (isNavigationCollapsed) {
      // Expand navigation when unfocusing a worksheet
      toggleNavigation();
    }
  }, [isNavigationCollapsed, toggleNavigation]);

  const baseScenario = scenariosProp.find((s) => s.is_system) || scenariosProp[0];
  const capacityModel = capacityModelQuery.data;
  const quotaModel = quotaModelQuery.data;

  const contextValue = useMemo(
    () => ({
      planningCycle,
      setPlanningCycle,
      capacityModel,
      quotaModel,
      capacityWorksheets: capacityWorksheetsQuery.data?.data ?? [],
      quotaWorksheets: quotaWorksheetsQuery.data?.data ?? [],
      scenarios: scenariosProp,
      baseScenario,
      worksheetsFilter,
      setWorksheetsFilter,
      isNavigationCollapsed,
      isAnimating,
      toggleNavigation,
      focusedWorksheet,
      focusWorksheet,
      unfocusWorksheet,
      planMetricsWorksheet,
      rosterWorksheet,
    }),
    [
      planningCycle,
      setPlanningCycle,
      capacityModel,
      quotaModel,
      capacityWorksheetsQuery.data?.data,
      quotaWorksheetsQuery.data?.data,
      scenariosProp,
      baseScenario,
      worksheetsFilter,
      setWorksheetsFilter,
      isNavigationCollapsed,
      isAnimating,
      toggleNavigation,
      focusedWorksheet,
      focusWorksheet,
      unfocusWorksheet,
      planMetricsWorksheet,
      rosterWorksheet,
    ],
  );

  // We know these models will exist because we create them in the backend upon creating a plan
  const isLoading =
    capacityModelQuery.isLoading ||
    quotaModelQuery.isLoading ||
    capacityWorksheetsQuery.isLoading ||
    quotaWorksheetsQuery.isLoading;
  if (isLoading) {
    return (
      <Box alignItems="center" display="flex" h="full" justifyContent="center" w="full">
        <Spinner />
      </Box>
    );
  }

  if (!capacityModel || !quotaModel) {
    return (
      <Box p={8} textAlign="center">
        <Text>{t('SPM.PlanDetailView.error')}</Text>
      </Box>
    );
  }

  return <SPMPlanContext.Provider value={contextValue as SPMPlanContextType}>{children}</SPMPlanContext.Provider>;
};
